import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { submitInquiry } from "../../lib/api";
import { COURSE_OPTIONS } from "../../lib/constants";
import { Loader2, Send } from "lucide-react";

const InquiryForm = ({ variant = "glass", showMessage = false, onSuccess }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", course: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.course) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry(form);
      toast.success("Inquiry submitted! Our counsellor will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", course: "", message: "" });
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const wrapClass =
    variant === "glass"
      ? "glass rounded-3xl p-6 md:p-8 shadow-[0_18px_50px_rgba(11,59,140,0.18)]"
      : "bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-xl shadow-blue-900/5";

  const labelClass = "text-xs font-semibold tracking-wider uppercase text-slate-500";
  const inputClass = "bg-white/90 border-slate-200 focus-visible:ring-megha-accent rounded-xl h-11";

  return (
    <form onSubmit={submit} className={wrapClass} data-testid="inquiry-form">
      <div className="mb-5">
        <div className="text-xs font-semibold tracking-[0.18em] uppercase text-megha-accent">Quick Inquiry</div>
        <h3 className="text-2xl font-extrabold text-megha-text mt-1 font-heading">
          Reserve your seat — 2026
        </h3>
        <p className="text-sm text-slate-500 mt-1">A counsellor will call you within 24 hours.</p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div>
          <label className={labelClass}>Full Name *</label>
          <Input
            data-testid="inquiry-name"
            placeholder="Your name"
            value={form.name}
            onChange={update("name")}
            className={inputClass}
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Phone *</label>
            <Input
              data-testid="inquiry-phone"
              type="tel"
              placeholder="+91"
              value={form.phone}
              onChange={update("phone")}
              className={inputClass}
              required
            />
          </div>
          <div>
            <label className={labelClass}>Email *</label>
            <Input
              data-testid="inquiry-email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={update("email")}
              className={inputClass}
              required
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Preferred Course *</label>
          <Select value={form.course} onValueChange={update("course")}>
            <SelectTrigger data-testid="inquiry-course" className={inputClass}>
              <SelectValue placeholder="Choose a B.Tech program" />
            </SelectTrigger>
            <SelectContent>
              {COURSE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} data-testid={`course-opt-${opt.value}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showMessage && (
          <div>
            <label className={labelClass}>Message (optional)</label>
            <Textarea
              data-testid="inquiry-message"
              placeholder="Anything you'd like us to know?"
              value={form.message}
              onChange={update("message")}
              className="bg-white/90 border-slate-200 focus-visible:ring-megha-accent rounded-xl min-h-[88px]"
            />
          </div>
        )}

        <Button
          data-testid="btn-submit-inquiry"
          type="submit"
          disabled={loading}
          className="mt-2 h-12 rounded-full bg-gradient-to-r from-megha-primary to-megha-accent hover:opacity-95 text-white text-base font-bold shadow-lg shadow-blue-900/25 hover:-translate-y-0.5 transition-all"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Apply for Admission</>}
        </Button>
        <p className="text-[11px] text-slate-500 text-center mt-1">
          By submitting you agree to be contacted by MIETW Admissions.
        </p>
      </div>
    </form>
  );
};

export default InquiryForm;
